import { IntakeItem } from '@billing/validation';
import { OptionsFilter } from '@mantine/core';

export type TranslateFn = (key: string, params?: any) => string;

export type UploadSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export const UPLOAD_LINK_PAGE_SIZE = 50;

export const passthroughOptionsFilter: OptionsFilter = ({ options }) => options;

export const formatProviderName = (item: IntakeItem) => {
  const provider = item.provider;
  if (!provider) return 'Unassigned';
  return [provider.title, provider.firstName, provider.lastName]
    .filter((part) => part && part.trim())
    .join(' ');
};

export const formatUploadLabel = (item: IntakeItem, translate: TranslateFn) => {
  const intakeNumber =
    item.intakeNumber === null || item.intakeNumber === undefined
      ? 'N/A'
      : item.intakeNumber;
  return (
    translate('pages:claims.table.columns.linkedUpload.uploadNumberLabel', {
      intakeNumber,
    }) || `Upload #${intakeNumber}`
  );
};

export const formatUploadLabelFromIntakeNumber = (
  intakeNumber: string,
  translate: TranslateFn
) => {
  if (!intakeNumber.trim()) return '';
  return (
    translate('pages:claims.table.columns.linkedUpload.uploadNumberLabel', {
      intakeNumber,
    }) || `Upload #${intakeNumber}`
  );
};

export const matchesUploadSearch = (
  item: IntakeItem,
  searchValue: string,
  translate: TranslateFn
) => {
  const normalizedSearch = searchValue.trim().toLowerCase();
  if (!normalizedSearch) return false;

  const providerName = formatProviderName(item).toLowerCase();
  const intakeNumber = String(item.intakeNumber ?? '').toLowerCase();
  const uploadLabel = formatUploadLabel(item, translate).toLowerCase();
  const itemID = item.id.toLowerCase();

  return (
    itemID.includes(normalizedSearch) ||
    providerName.includes(normalizedSearch) ||
    intakeNumber.includes(normalizedSearch) ||
    uploadLabel.includes(normalizedSearch)
  );
};

export const dedupeItemsByID = (items: IntakeItem[]) => {
  const byID = new Map<string, IntakeItem>();
  items.forEach((item) => {
    byID.set(item.id, item);
  });
  return Array.from(byID.values());
};

export const appendUniqueItemsByID = (
  current: IntakeItem[],
  incoming: IntakeItem[]
) => {
  if (!incoming.length) return current;

  const byID = new Map(current.map((item) => [item.id, item]));
  let changed = false;

  incoming.forEach((item) => {
    if (!byID.has(item.id)) changed = true;
    byID.set(item.id, item);
  });

  return changed ? Array.from(byID.values()) : current;
};

export const hasSameItemIDs = (a: IntakeItem[], b: IntakeItem[]) => {
  if (a.length !== b.length) return false;
  return a.every((item, index) => item.id === b[index]?.id);
};

export const upsertUploadOption = ({
  item,
  translate,
  optionsByID,
  providerByID,
}: {
  item: IntakeItem;
  translate: TranslateFn;
  optionsByID: Map<string, UploadSelectOption>;
  providerByID: Map<string, string>;
}) => {
  optionsByID.set(item.id, {
    value: item.id,
    label: formatUploadLabel(item, translate),
  });
  providerByID.set(item.id, formatProviderName(item));
};
